import { UnsuccessfulFetchError } from "../clients/common/errors";

import { useEffect, useState, useRef, useCallback } from "react";

import {
    OfferingHamsMap,
    OfferingSKU,
    OfferingSKUMap,
    PricingPlans,
    PricingStrategyValue,
    PricingTier,
    PricingTierPrice,
    CloudProductKey,
    parsePricingStrategy,
    CloudOfferingKey,
    CloudProductValues,
    DataCenterProductValues
} from "@/components/wpl/pricing/src/price-finder/constants";
export interface ContentfulEntryLink {
    sys: {
        type: string;
        linkType: string;
        id: string;
    };
}

export interface ContentfulEntry<T> {
    sys: {
        id: string;
    };
    fields: T;
}

export type APICallStatus = "idle" | "fetching" | "complete";
export interface ContentfulPricingPlan {
    product?: ContentfulEntryLink;
    periodUnit?: string;
    periodCount?: number;
    status?: string;
    edition?: string;
    configuration: PricingPlan[];
    pricingPlanId: string;
    sourceSequence?: number;
    source?: string;
    type?: string;
}

export type CCPPricingPlanContent = {
    id: string;
    eventTimestamp: string;
    key: string;
    offeringKey: string;
    currency: string;
    type: string;
    primaryCycle_name: string;
    primaryCycle_interval: string;
    primaryCycle_count: string;
    status: string;
    activatedWithReason: string;
    items: CCPPricingPlanItems;
    ari: string;
    offeringName: string;
};

export type EntitlementTemplateData = {
    id: string;
    features: Array<{
        featureId: string;
        domain: string;
        value: {
            allowances: Array<{
                reference: string;
                type: string;
                config: {
                    usageKey: string;
                    cycles: Array<{
                        schedule: string;
                        cycle: string;
                        amount: number;
                        relativeTo: {
                            referenceType: string;
                            referenceValue: string;
                        };
                    }>;
                    isConsumer: boolean;
                    recipientEntity: string;
                };
            }>;
        };
    }>;
};

export type CCPPricingPlanItems = ReadonlyArray<CCPPricingPlanItem>;

export type CCPPricingPlanItem = {
    chargeType: string;
    chargeElement: string;
    tiersMode: string;

    cycle: {
        name: string;
        interval: string;
        count: string;
    };

    prorateOnUsageChange: string;

    tiers: CCPPricingPlanTier[];
};

export type CCPPricingPlanTier = {
    floor: number;
    ceiling: number;
    unitAmount: number;
    flatAmount: number;
};

export type CCPOfferingContent = {
    id: string;
    eventTimestamp: string;
    key: string;
    name?: string;
    productKey?: string;
    pricingType?: string;
    dependsOnOfferings?: string;
    trial_lengthDays: string;
    sku?: string;
    ari?: string;
    level?: string;
    status?: string;
    hostingType?: string;
    slugs?: string[];
    defaultPricingPlans: CCPPricingPlanContent[] | null;
    entitlementTemplate_data?: EntitlementTemplateData;
};
export interface ContentfulProduct {
    name?: string;
    platform?: ContentfulEntryLink;
    catalogItemId?: string;
    description?: string;
    productFamily?: string;
    productType?: string;
    unitLabel?: string;
    billingType?: string;
    vendor?: ContentfulEntryLink;
    parentProducts?: ContentfulEntryLink[];
    key: string;
    sourceSequence?: number;
    source?: string;
    defaultPricingPlanMonthly?: ContentfulEntryLink;
    defaultPricingPlanYearly?: ContentfulEntryLink;
    parentProductName?: string;
    defaultStdPricingPlanMonthly?: ContentfulEntryLink;
    defaultStdPricingPlanYearly?: ContentfulEntryLink;
    defaultFreePricingPlanMonthly?: ContentfulEntryLink;
    defaultFreePricingPlanYearly?: ContentfulEntryLink;
    defaultPremiumPricingPlanMonthly?: ContentfulEntryLink;
    defaultPremiumPricingPlanYearly?: ContentfulEntryLink;
    defaultEnterprisePricingPlanMonthly?: ContentfulEntryLink;
    defaultEnterprisePricingPlanYearly?: ContentfulEntryLink;
    isHidden?: string;
    isFree?: string;
    isDefaultStdPricingPlanMonthlyFree?: string;
    isDefaultStdPricingPlanYearlyFree?: string;
    isDefaultFreePricingPlanMonthlyFree?: string;
    isDefaultFreePricingPlanYearlyFree?: string;
    isDefaultPremiumPricingPlanMonthlyFree?: string;
    isDefaultPremiumPricingPlanYearlyFree?: string;
}

interface PricingPlanPrices {
    unit_price: number;
    currency: "USD" | "JPY";
}

export interface PricingPlan {
    prices: PricingPlanPrices[];
    pricing_strategy: PricingStrategyValue;
    unit_description: string;
    unit_limit_lower: number;
    unit_limit_upper: number;
}

export interface PricingCollection {
    free?: PricingPlan[];
    standard?: PricingPlan[];
    premium?: PricingPlan[];
    enterprise?: PricingPlan[];
}

export interface AppPricingData {
    localizedPriceBlock?: {
        confluence?: PricingCollection;
    };
    ccpPricingData?: {
        jiraSoftware?: OfferingSKUMap<CloudOfferingKey, CCPOfferingContent>;
        confluence?: OfferingSKUMap<CloudOfferingKey, CCPOfferingContent>;
        jiraServiceDesk?: OfferingSKUMap<CloudOfferingKey, CCPOfferingContent>;
        compass?: OfferingSKUMap<CloudOfferingKey, CCPOfferingContent>;
        jiraProductDiscovery?: OfferingSKUMap<
            CloudOfferingKey,
            CCPOfferingContent
        >;
        guard?: OfferingSKUMap<CloudOfferingKey, CCPOfferingContent>;
        rovo?: OfferingSKUMap<CloudOfferingKey, CCPOfferingContent>;
        trello?: OfferingSKUMap<CloudOfferingKey, CCPOfferingContent>;
        loom?: OfferingSKUMap<CloudOfferingKey, CCPOfferingContent>;
        strategyCollection?: OfferingSKUMap<
            CloudOfferingKey,
            CCPOfferingContent
        >;
        teamworkCollection?: OfferingSKUMap<
            CloudOfferingKey,
            CCPOfferingContent
        >;
    };
    hamsPricingData?: {
        jiraSoftware?: OfferingHamsMap<CloudProductKey, PricingPlans>;
        confluence?: OfferingHamsMap<CloudProductKey, PricingPlans>;
        jiraServiceDesk?: OfferingHamsMap<CloudProductKey, PricingPlans>;
        bitbucket?: OfferingHamsMap<CloudProductKey, PricingPlans>;
        guard?: OfferingHamsMap<CloudProductKey, PricingPlans>;
    };
    localizedPriceBlockDropdown?: {
        jiraSoftware?: PricingPlan[];
        confluence?: PricingPlan[];
        jiraServiceDesk?: PricingPlan[];
        bitbucket?: PricingPlan[];
    };
}

enum EDITION_SUFFIXES {
    FREE = ".free",
    STANDARD = "",
    PREMIUM = ".premium",
    ENTERPRISE = ".enterprise"
}

type EditionSuffix = { [P in keyof EDITION_SUFFIXES]: EDITION_SUFFIXES[P] };

const NON_STANDARD_EDITION_SUFFIXES = [
    EDITION_SUFFIXES.FREE,
    EDITION_SUFFIXES.PREMIUM,
    EDITION_SUFFIXES.ENTERPRISE
];

export class ProductCatalogClient {
    private readonly accessToken: string;
    private readonly space: string;
    private readonly environment: string;

    public constructor(
        accessToken: string,
        space: string,
        environment = "master"
    ) {
        this.accessToken = accessToken;
        this.space = space;
        this.environment = environment;
    }

    private getUrl() {
        return `https://cdn.contentful.com/spaces/${this.space}/environments/${this.environment}`;
    }

    public async getEntries(queryParams: string) {
        const url = this.getUrl();
        const response = await fetch(
            `${url}/entries?access_token=${this.accessToken}&${queryParams}`
        );
        if (response.ok) return response.json();
        throw new UnsuccessfulFetchError({
            message: "Unable to fetch prices",
            response
        });
    }

    /**
     *
     * @param key the `key` property of the HAMS Product
     * @returns Monthly prices for each headcount bucket for the free, standard and premium tiers
     */

    //TODO: Update to accept CCP Offering Sku
    public async getLocalizedPriceBlockPrices(
        key: CloudProductValues
    ): Promise<PricingCollection> {
        const pricingQuery = `content_type=product&select=fields.key%2Cfields.defaultStdPricingPlanMonthly%2Cfields.defaultPremiumPricingPlanMonthly%2Cfields.defaultFreePricingPlanMonthly%2Cfields.defaultEnterprisePricingPlanMonthly%2Csys.id%2Csys.type&fields.key%5Bin%5D=${key}&include=2`;
        const { includes, items } = (await this.getEntries(pricingQuery)) as {
            includes: { Entry: ContentfulEntry<ContentfulPricingPlan>[] };
            items: ContentfulEntry<ContentfulProduct>[];
        };
        if (!items.length) {
            throw new Error("No pricing plans match this key");
        }
        const parentPricingPlan =
            items[0] as ContentfulEntry<ContentfulProduct>;

        const freeMonthly = includes.Entry.find(
            (entry) =>
                entry.sys.id ===
                parentPricingPlan.fields.defaultFreePricingPlanMonthly?.sys.id
        );
        const standardMonthly = includes.Entry.find(
            (entry) =>
                entry.sys.id ===
                parentPricingPlan.fields.defaultStdPricingPlanMonthly?.sys.id
        );
        const premiumMonthly = includes.Entry.find(
            (entry) =>
                entry.sys.id ===
                parentPricingPlan.fields.defaultPremiumPricingPlanMonthly?.sys
                    .id
        );
        return {
            free: freeMonthly?.fields.configuration,
            standard: standardMonthly?.fields.configuration,
            premium: premiumMonthly?.fields.configuration
        };
    }

    public async getLocalizedPriceBlockDropdown(key: DataCenterProductValues) {
        const pricingQuery = `content_type=product&select=fields.key%2Cfields.defaultStdPricingPlanMonthly%2Cfields.defaultStdPricingPlanYearly%2Cfields.defaultPremiumPricingPlanMonthly%2Cfields.defaultPremiumPricingPlanYearly%2Cfields.defaultFreePricingPlanMonthly%2Cfields.defaultEnterprisePricingPlanMonthly%2Cfields.defaultEnterprisePricingPlanYearly%2Csys.id%2Csys.type&fields.key%5Bin%5D=${key}`;
        const { includes, items } = (await this.getEntries(pricingQuery)) as {
            includes: { Entry: ContentfulEntry<ContentfulPricingPlan>[] };
            items: ContentfulEntry<ContentfulProduct>[];
        };
        if (!items.length || !includes) {
            throw new Error("No pricing plans match this key");
        }
        const fields = includes.Entry[0]?.fields.configuration as PricingPlan[];
        return fields;
    }

    /**
     *
     * @param key the `key` property of the HAMS Product
     * @returns Monthly prices for each headcount bucket for the free, standard and premium tiers
     */
    public async getHAMSPrices(
        key: CloudProductValues
    ): Promise<OfferingHamsMap<CloudProductKey, PricingPlans>> {
        const pricingQuery = `content_type=product&select=fields.key%2Cfields.defaultStdPricingPlanMonthly%2Cfields.defaultStdPricingPlanYearly%2Cfields.defaultPremiumPricingPlanMonthly%2Cfields.defaultPremiumPricingPlanYearly%2Cfields.defaultFreePricingPlanMonthly%2Cfields.defaultEnterprisePricingPlanMonthly%2Cfields.defaultEnterprisePricingPlanYearly%2Csys.id%2Csys.type&fields.key%5Bin%5D=${key}`;
        const { includes, items } = (await this.getEntries(pricingQuery)) as {
            includes: { Entry: ContentfulEntry<ContentfulPricingPlan>[] };
            items: ContentfulEntry<ContentfulProduct>[];
        };
        if (!items.length) {
            throw new Error(`No HAMS plans match this key: ${key}`);
        }

        const productKeys = [
            key + EDITION_SUFFIXES.FREE,
            key,
            key + EDITION_SUFFIXES.PREMIUM,
            key + EDITION_SUFFIXES.ENTERPRISE
        ];

        const removeEditionSuffix = (productKey: string) =>
            productKey.replace(
                new RegExp(
                    NON_STANDARD_EDITION_SUFFIXES.map(
                        (suffix) => `\\${suffix}`
                    ).join("|") + "$"
                ),
                ""
            );

        const getPricingPlansForEditionSuffixedKey = (
            editionSuffixedProductKey: string
        ): PricingPlans => {
            const matchedEditionSuffix: EditionSuffix =
                NON_STANDARD_EDITION_SUFFIXES.reduce(
                    (matchingEditionSuffix, editionSuffix) =>
                        editionSuffixedProductKey.endsWith(editionSuffix)
                            ? editionSuffix
                            : matchingEditionSuffix,
                    EDITION_SUFFIXES.STANDARD
                );

            const parentPricingPlan =
                items[0] as ContentfulEntry<ContentfulProduct>;

            const freeMonthly = includes.Entry.find(
                (entry) =>
                    entry.sys.id ===
                    parentPricingPlan.fields.defaultFreePricingPlanMonthly?.sys
                        .id
            );

            const freeAnnual = includes.Entry.find(
                (entry) =>
                    entry.sys.id ===
                    parentPricingPlan.fields.defaultFreePricingPlanYearly?.sys
                        .id
            );
            const standardMonthly = includes.Entry.find(
                (entry) =>
                    entry.sys.id ===
                    parentPricingPlan.fields.defaultStdPricingPlanMonthly?.sys
                        .id
            );
            const premiumMonthly = includes.Entry.find(
                (entry) =>
                    entry.sys.id ===
                    parentPricingPlan.fields.defaultPremiumPricingPlanMonthly
                        ?.sys.id
            );
            const enterpriseMonthly = includes.Entry.find(
                (entry) =>
                    entry.sys.id ===
                    parentPricingPlan.fields.defaultEnterprisePricingPlanMonthly
                        ?.sys.id
            );
            const standardAnnual = includes.Entry.find(
                (entry) =>
                    entry.sys.id ===
                    parentPricingPlan.fields.defaultStdPricingPlanYearly?.sys.id
            );
            const premiumAnnual = includes.Entry.find(
                (entry) =>
                    entry.sys.id ===
                    parentPricingPlan.fields.defaultPremiumPricingPlanYearly
                        ?.sys.id
            );

            const enterpriseAnnual = includes.Entry.find(
                (entry) =>
                    entry.sys.id ===
                    parentPricingPlan.fields.defaultEnterprisePricingPlanYearly
                        ?.sys.id
            );

            let monthly: PricingPlan[] | undefined = undefined;
            let annual: PricingPlan[] | undefined = undefined;

            switch (matchedEditionSuffix) {
                case EDITION_SUFFIXES.FREE:
                    monthly = freeMonthly?.fields.configuration;
                    annual = freeAnnual?.fields.configuration;
                    break;

                case EDITION_SUFFIXES.STANDARD:
                    monthly = standardMonthly?.fields.configuration;
                    annual = standardAnnual?.fields.configuration;
                    break;

                case EDITION_SUFFIXES.PREMIUM:
                    monthly = premiumMonthly?.fields.configuration;
                    annual = premiumAnnual?.fields.configuration;
                    break;

                case EDITION_SUFFIXES.ENTERPRISE:
                    monthly = enterpriseMonthly?.fields.configuration;
                    annual = enterpriseAnnual?.fields.configuration;
                    break;
            }

            return {
                monthly: monthly?.map(mapPricingPlanToPricingTier),
                annual: annual?.map(mapPricingPlanToPricingTier)
            };
        };

        let ret: Partial<OfferingHamsMap<CloudOfferingKey, PricingPlans>> = {};
        for (const productKey of productKeys) {
            const editionlessKey = removeEditionSuffix(productKey);
            const entry = items.find(
                ({ fields: { key } }) => key === editionlessKey
            );
            if (entry) {
                ret = {
                    ...ret,
                    [productKey]:
                        getPricingPlansForEditionSuffixedKey(productKey)
                };
            }
        }

        return ret as OfferingHamsMap<CloudProductKey, PricingPlans>;
    }

    /**
     *
     * @param offeringSKUs the `key` property of the CCP Product
     * @returns A map of offeringSkus to their respective product plan content
     */
    public async getCCPPricingData(
        offeringSKUs: OfferingSKU<CloudOfferingKey>[]
    ): Promise<OfferingSKUMap<CloudOfferingKey, CCPOfferingContent>> {
        const mappedOfferings = offeringSKUs
            .map((offeringSku, idx) => {
                if (idx > 0) {
                    return `%2C${offeringSku}`;
                } else {
                    return `=${offeringSku}`;
                }
            })
            .join("");
        const ccpPricingDataQuery = `content_type=31uDFGQ9rMyZpBgP4oexja&fields.sku%5Bin%5D${mappedOfferings}`;
        const { items, includes } = (await this.getEntries(
            ccpPricingDataQuery
        )) as {
            items: ContentfulEntry<CCPOfferingContent>[];
            includes: { Entry: ContentfulEntry<CCPPricingPlanContent>[] };
        };
        if (!items.length) {
            throw new Error("No offerings match this key");
        }

        const ret: Partial<
            OfferingSKUMap<CloudOfferingKey, CCPOfferingContent>
        > = {};

        for (const offeringSKU of offeringSKUs) {
            const entry = items.find(
                ({ fields: { sku } }) => sku === offeringSKU
            );

            const contentfulOffering = entry?.fields;
            const pricingPlans = includes.Entry.map((includedEntry) => {
                if (
                    includedEntry.fields.offeringName ===
                    contentfulOffering?.name
                ) {
                    return includedEntry.fields;
                } else {
                    return undefined;
                }
            }).filter(Boolean) as CCPPricingPlanContent[];
            contentfulOffering?.defaultPricingPlans?.splice(
                0,
                contentfulOffering.defaultPricingPlans.length,
                ...pricingPlans
            );

            ret[offeringSKU] = contentfulOffering;
        }

        return ret as OfferingSKUMap<CloudOfferingKey, CCPOfferingContent>;
    }
}

export function useCCPPriceFinderData(
    offeringSKUs: OfferingSKU<CloudOfferingKey>[],
    accessToken: string,
    space: string,
    environment?: string
) {
    const offeringSKUsCount = offeringSKUs.length;

    const [status, setStatus] = useState<APICallStatus>("idle");

    const [prices, setPrices] =
        useState<OfferingSKUMap<CloudOfferingKey, CCPOfferingContent>>();
    const client: ProductCatalogClient = useRef(
        new ProductCatalogClient(accessToken, space, environment)
    ).current;

    const fetchData = useCallback(async () => {
        if (offeringSKUsCount) {
            setStatus("fetching");
            const retrievedPrices = await client.getCCPPricingData(
                offeringSKUs
            );
            setPrices(retrievedPrices);
            setStatus("complete");
        } else {
            setStatus("complete");
            setPrices({});
        }
    }, [offeringSKUsCount]);

    return { status, prices, fetchData };
}

export function useHAMSPriceData(
    key: CloudProductValues,
    accessToken: string,
    space: string,
    environment?: string
) {
    const [status, setStatus] = useState<APICallStatus>("idle");
    const [prices, setPrices] =
        useState<OfferingHamsMap<CloudProductKey, PricingPlans>>();
    const client: ProductCatalogClient = new ProductCatalogClient(
        accessToken,
        space,
        environment
    );
    useEffect(() => {
        const fetchData = async () => {
            setStatus("fetching");
            const retrievedPrices = await client.getHAMSPrices(key);
            setPrices(retrievedPrices);
            setStatus("complete");
        };

        if (key) {
            fetchData();
        } else {
            setStatus("complete");
            setPrices({} as OfferingHamsMap<CloudProductKey, PricingPlans>);
        }
    }, [key]);

    return { status, prices };
}

export function useLocalizedPriceData(
    key: CloudProductValues,
    accessToken: string,
    space: string,
    environment?: string
) {
    const [status, setStatus] = useState<APICallStatus>("idle");
    const [prices, setPrices] = useState<PricingCollection>();
    const client = new ProductCatalogClient(accessToken, space, environment);

    useEffect(() => {
        const fetchData = async () => {
            setStatus("fetching");
            const retrievedPrices = await client.getLocalizedPriceBlockPrices(
                key
            );
            setPrices(retrievedPrices);
            setStatus("complete");
        };

        fetchData();
    }, []);

    return { status, prices };
}

//TODO: Do we need this? Can we just use the types already declared and reuse them?
function mapPricingPlanToPricingTier({
    pricing_strategy,
    unit_description,
    unit_limit_lower,
    unit_limit_upper,
    prices
}: PricingPlan): PricingTier {
    return {
        pricingStrategy: parsePricingStrategy(pricing_strategy),
        unitDescription: unit_description,
        unitLimitLower: unit_limit_lower,
        unitLimitUpper: unit_limit_upper,
        prices: prices.map(mapPricingPlanPricesItemPriceToPricingTierPrice)
    };
}

function mapPricingPlanPricesItemPriceToPricingTierPrice({
    currency,
    unit_price
}: PricingPlanPrices): PricingTierPrice {
    return {
        currency,
        unitPrice: unit_price
    };
}

export function useLocalizedPriceDataDropdown(
    key: DataCenterProductValues,
    accessToken: string,
    space: string,
    environment?: string
) {
    const [status, setStatus] = useState<APICallStatus>("idle");
    const [prices, setPrices] = useState<PricingPlan[]>();
    const client: ProductCatalogClient = new ProductCatalogClient(
        accessToken,
        space,
        environment
    );
    useEffect(() => {
        const fetchData = async () => {
            setStatus("fetching");
            const retrievedPrices = await client.getLocalizedPriceBlockDropdown(
                key
            );
            setPrices(retrievedPrices);
            setStatus("complete");
        };
        fetchData();
    }, []);
    return { status, prices };
}
